import { useMemo, useCallback, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  REQUIRED_MSG,
  OPTION_MSG,
  CHECK_BOX_MSG,
} from "../schema/formik";
import {
  saveMisdeliveryAudit,
  MisdeliveryAuditPayload,
} from "../API/MisdeliveryAudit";

const MAX_TEXT_LENGTH = 100;

// Helper to convert "Y" to "Y", anything else to undefined (for optional Y/N fields)
const yOrUndefined = (value: string | undefined): string | undefined => (value === "Y" ? "Y" : undefined);

// Helper to convert "Y" to "Y", "N" to "N", anything else to undefined (for paired Y/N fields)
const yOrN = (value: string | undefined): string | undefined => (value === "Y" || value === "N" ? value : undefined);

// Helper to coerce numeric text input to number for API payloads.
// Sends 0 when the field is blank.
const numberOrZero = (value: string | undefined): number | undefined => {
  if (value === undefined || value === null) return 0;
  const text = String(value).trim();
  if (!text) return 0;

  const parsed = Number(text);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const textOrZero = (value: string | undefined): string => {
  if (value === undefined || value === null) return "0";
  const text = String(value).trim();
  return text || "0";
};

// Transform form values to API payload format
export const transformFormValuesToPayload = (
  values: MisdeliveryAuditFormValues
): MisdeliveryAuditPayload => {
  const isRecoveredN = values.didSaiaRecoverMisdelvFreight === "N";
  const isRecoveredY = values.didSaiaRecoverMisdelvFreight === "Y";

  // When recovered = N, only include the 3 relevant fields
  if (isRecoveredN) {
    return {
      didSaiaRecoverMisdelvFreight: "N",
      didSaiaRecoverMisdelvFreightWhy: values.didSaiaRecoverMisdelvFreightWhy || undefined,
      notRecoveredFreightStatementOrDrImaged: yOrUndefined(values.notRecoveredFreightStatementOrDrImaged),
    };
  }

  // When recovered = Y or not set, include all Y-related fields
  return {
    didSaiaRecoverMisdelvFreight: values.didSaiaRecoverMisdelvFreight || undefined,
    // N-specific fields - only include if not Y
    didSaiaRecoverMisdelvFreightWhy: !isRecoveredY ? (values.didSaiaRecoverMisdelvFreightWhy || undefined) : undefined,
    notRecoveredFreightStatementOrDrImaged: !isRecoveredY ? yOrUndefined(values.notRecoveredFreightStatementOrDrImaged) : undefined,
    // Y-specific fields
    recoveredFreightNewProNumber: isRecoveredY
      ? numberOrZero(values.recoveredFreightNewProNumber)
      : undefined,
    // BOL fields - use yOrN to send both Y and N values
    recoveredFreightSaiaMisdelvBol: isRecoveredY ? yOrN(values.recoveredFreightSaiaMisdelvBol) : undefined,
    saiaMisdelvBolConfirmBilling: isRecoveredY ? yOrUndefined(values.saiaMisdelvBolConfirmBilling) : undefined,
    saiaMisdelvBolConfirmBillingDetail: isRecoveredY ? (values.saiaMisdelvBolConfirmBillingDetail || undefined) : undefined,
    saiaMisdelvBolConfirmImaged: isRecoveredY ? yOrUndefined(values.saiaMisdelvBolConfirmImaged) : undefined,
    saiaMisdelvBolConfirmImagedDetail: isRecoveredY ? (values.saiaMisdelvBolConfirmImagedDetail || undefined) : undefined,
    recoveredFreightCustomerProvidedBol: isRecoveredY ? yOrN(values.recoveredFreightCustomerProvidedBol) : undefined,
    customerProvidedBolConfirmBilling: isRecoveredY ? yOrUndefined(values.customerProvidedBolConfirmBilling) : undefined,
    customerProvidedBolConfirmBillingDetail: isRecoveredY ? (values.customerProvidedBolConfirmBillingDetail || undefined) : undefined,
    customerProvidedBolConfirmImaged: isRecoveredY ? yOrUndefined(values.customerProvidedBolConfirmImaged) : undefined,
    customerProvidedBolConfirmImagedDetail: isRecoveredY ? (values.customerProvidedBolConfirmImagedDetail || undefined) : undefined,
    recoveredFreightNewProAllShort: isRecoveredY ? (values.recoveredFreightNewProAllShort || undefined) : undefined,
    recoveredFreightNewProShowsProOrNbOv: isRecoveredY ? yOrUndefined(values.recoveredFreightNewProShowsProOrNbOv) : undefined,
    newProShowsProOrNbOvDetail: isRecoveredY ? (values.newProShowsProOrNbOvDetail || undefined) : undefined,
    recoveredFreightConfirmDrImaged: isRecoveredY ? yOrUndefined(values.recoveredFreightConfirmDrImaged) : undefined,
    drImagedSignedShowingAppliesToProOrNbOv: isRecoveredY ? (values.drImagedSignedShowingAppliesToProOrNbOv || undefined) : undefined,
    signedAppliesToProOrNbOvExceptionNumber: isRecoveredY ? (values.signedAppliesToProOrNbOvExceptionNumber || undefined) : undefined,
    // Non-N fields (shown when recovered !== N)
    didMisdelvPickupLeaveConsigneeShort: !isRecoveredN ? (values.didMisdelvPickupLeaveConsigneeShort || undefined) : undefined,
    misdelvPickupConsigneeShortAdditionalComments: !isRecoveredN ? (values.misdelvPickupConsigneeShortAdditionalComments || undefined) : undefined,
    didRepresentativeUpdateDeliveryStatus: !isRecoveredN ? (values.didRepresentativeUpdateDeliveryStatus || undefined) : undefined,
    representativeDeliveryStatusAdditionalComments: !isRecoveredN ? (values.representativeDeliveryStatusAdditionalComments || undefined) : undefined,
    didMisdelvFreightMoveCorrectly: !isRecoveredN ? (values.didMisdelvFreightMoveCorrectly || undefined) : undefined,
    // PRO type fields - use yOrN to send both Y and N values
    misdelvFreightMoveOriginalPro: !isRecoveredN ? yOrN(values.misdelvFreightMoveOriginalPro) : undefined,
    misdelvFreightMoveSuffixPro: !isRecoveredN ? yOrN(values.misdelvFreightMoveSuffixPro) : undefined,
    correctConsigneeReceivedCompleteOrder: !isRecoveredN ? (values.correctConsigneeReceivedCompleteOrder || undefined) : undefined,
    correctConsigneeOrderDoesDeliveryStatusShowOpenShortage: !isRecoveredN ? (values.correctConsigneeOrderDoesDeliveryStatusShowOpenShortage || undefined) : undefined,
    misdelvFreightMoveCorrectlyAdditionalComments: !isRecoveredN ? (values.misdelvFreightMoveCorrectlyAdditionalComments || undefined) : undefined,
    recoveredFreightConfirmOption14: !isRecoveredN ? yOrUndefined(values.recoveredFreightConfirmOption14) : undefined,
    recoveredFreightConfirmF18NotesClosingStatement: !isRecoveredN ? yOrUndefined(values.recoveredFreightConfirmF18NotesClosingStatement) : undefined,
    recoveredFreightMsdExceptionAppliesToPro: !isRecoveredN ? yOrUndefined(values.recoveredFreightMsdExceptionAppliesToPro) : undefined,
  };
};

export type MisdeliveryAuditFormValues = {
  // Main question
  didSaiaRecoverMisdelvFreight?: "" | "Y" | "N";
  
  // When recovered = N
  didSaiaRecoverMisdelvFreightWhy?: string;
  notRecoveredFreightStatementOrDrImaged?: "" | "Y" | "N";
  
  // When recovered = Y
  recoveredFreightNewProNumber?: string;
  
  // BOL selection - radio button (Y when selected, empty otherwise)
  recoveredFreightSaiaMisdelvBol?: "" | "Y";
  saiaMisdelvBolConfirmBilling?: "" | "Y" | "N";
  saiaMisdelvBolConfirmBillingDetail?: string;
  saiaMisdelvBolConfirmImaged?: "" | "Y" | "N";
  saiaMisdelvBolConfirmImagedDetail?: string;
  
  recoveredFreightCustomerProvidedBol?: "" | "Y";
  customerProvidedBolConfirmBilling?: "" | "Y" | "N";
  customerProvidedBolConfirmBillingDetail?: string;
  customerProvidedBolConfirmImaged?: "" | "Y" | "N";
  customerProvidedBolConfirmImagedDetail?: string;
  
  // NEW PRO questions
  recoveredFreightNewProAllShort?: "" | "Y" | "N";
  recoveredFreightNewProShowsProOrNbOv?: "" | "Y" | "N";
  newProShowsProOrNbOvDetail?: string;
  
  // DR Imaged questions
  recoveredFreightConfirmDrImaged?: "" | "Y" | "N";
  drImagedSignedShowingAppliesToProOrNbOv?: "" | "Y" | "N";
  signedAppliesToProOrNbOvExceptionNumber?: string;
  
  // Pickup questions
  didMisdelvPickupLeaveConsigneeShort?: "" | "Y" | "N";
  misdelvPickupConsigneeShortAdditionalComments?: string;
  didRepresentativeUpdateDeliveryStatus?: "" | "Y" | "N";
  representativeDeliveryStatusAdditionalComments?: string;
  
  // Freight move questions
  didMisdelvFreightMoveCorrectly?: "" | "Y" | "N";
  misdelvFreightMoveOriginalPro?: "" | "Y" | "N";
  misdelvFreightMoveSuffixPro?: "" | "Y" | "N";
  correctConsigneeReceivedCompleteOrder?: "" | "Y" | "N";
  correctConsigneeOrderDoesDeliveryStatusShowOpenShortage?: "" | "Y" | "N";
  misdelvFreightMoveCorrectlyAdditionalComments?: string;
  
  // Final checkboxes
  recoveredFreightConfirmOption14?: "" | "Y" | "N";
  recoveredFreightConfirmF18NotesClosingStatement?: "" | "Y" | "N";
  recoveredFreightMsdExceptionAppliesToPro?: "" | "Y" | "N";
  
  // Metadata (read-only, from API response)
  exceptionNumber?: number;
  addedTime?: string;
  updatedTime?: string;
  dateAdded?: string;
  dateUpdated?: string;
  entryUser?: string;
  updatedUser?: string;
};

export const misdeliveryAuditInitialValues: MisdeliveryAuditFormValues = {
  // Main question
  didSaiaRecoverMisdelvFreight: undefined,
  
  // When recovered = N
  didSaiaRecoverMisdelvFreightWhy: undefined,
  notRecoveredFreightStatementOrDrImaged: undefined,
  
  // When recovered = Y
  recoveredFreightNewProNumber: undefined,
  
  // SAIA BOL fields
  recoveredFreightSaiaMisdelvBol: undefined,
  saiaMisdelvBolConfirmBilling: undefined,
  saiaMisdelvBolConfirmBillingDetail: undefined,
  saiaMisdelvBolConfirmImaged: undefined,
  saiaMisdelvBolConfirmImagedDetail: undefined,
  
  // Customer BOL fields
  recoveredFreightCustomerProvidedBol: undefined,
  customerProvidedBolConfirmBilling: undefined,
  customerProvidedBolConfirmBillingDetail: undefined,
  customerProvidedBolConfirmImaged: undefined,
  customerProvidedBolConfirmImagedDetail: undefined,
  
  // NEW PRO questions
  recoveredFreightNewProAllShort: undefined,
  recoveredFreightNewProShowsProOrNbOv: undefined,
  newProShowsProOrNbOvDetail: undefined,
  
  // DR Imaged questions
  recoveredFreightConfirmDrImaged: undefined,
  drImagedSignedShowingAppliesToProOrNbOv: undefined,
  signedAppliesToProOrNbOvExceptionNumber: undefined,
  
  // Pickup questions
  didMisdelvPickupLeaveConsigneeShort: undefined,
  misdelvPickupConsigneeShortAdditionalComments: undefined,
  didRepresentativeUpdateDeliveryStatus: undefined,
  representativeDeliveryStatusAdditionalComments: undefined,
  
  // Freight move questions
  didMisdelvFreightMoveCorrectly: undefined,
  misdelvFreightMoveOriginalPro: undefined,
  misdelvFreightMoveSuffixPro: undefined,
  correctConsigneeReceivedCompleteOrder: undefined,
  correctConsigneeOrderDoesDeliveryStatusShowOpenShortage: undefined,
  misdelvFreightMoveCorrectlyAdditionalComments: undefined,
  
  // Final checkboxes
  recoveredFreightConfirmOption14: undefined,
  recoveredFreightConfirmF18NotesClosingStatement: undefined,
  recoveredFreightMsdExceptionAppliesToPro: undefined,
};

export const misdeliveryAuditValidationSchema = Yup.object().shape({
  // Main question - always required
  didSaiaRecoverMisdelvFreight: Yup.string().trim().required(OPTION_MSG),

  // When didSaiaRecoverMisdelvFreight === "N"
  didSaiaRecoverMisdelvFreightWhy: Yup.string().trim()
    .max(MAX_TEXT_LENGTH, `Must be at most ${MAX_TEXT_LENGTH} characters`)
    .when("didSaiaRecoverMisdelvFreight", {
      is: "N",
      then: (schema) => schema.required(REQUIRED_MSG),
      otherwise: (schema) => schema.notRequired(),
    }),

  // Statement or DR imaged - required when didSaiaRecoverMisdelvFreight === "N"
  notRecoveredFreightStatementOrDrImaged: Yup.string().trim().when(
    "didSaiaRecoverMisdelvFreight",
    {
      is: "N",
      then: (schema) => schema.required(CHECK_BOX_MSG).oneOf(["Y"], CHECK_BOX_MSG),
      otherwise: (schema) => schema.notRequired(),
    }
  ),

  // When didSaiaRecoverMisdelvFreight === "Y"
  recoveredFreightNewProNumber: Yup.string().trim()
    .when("didSaiaRecoverMisdelvFreight", {
      is: "Y",
      then: (schema) =>
        schema
          .required(REQUIRED_MSG)
          .matches(/^\d+$/, "New PRO # must be numeric.")
          .min(11, "New PRO # must be at least 11 digits."),
      otherwise: (schema) => schema.notRequired(),
    }),

  // BOL selection validation - at least one must be "Y" when recovered = "Y"
  // We validate on recoveredFreightSaiaMisdelvBol using a test
  recoveredFreightSaiaMisdelvBol: Yup.string().trim().test(
    "bol-required",
    OPTION_MSG,
    function (value) {
      const { didSaiaRecoverMisdelvFreight, recoveredFreightCustomerProvidedBol } = this.parent;
      // If recovered is Y, at least one BOL must be selected
      if (didSaiaRecoverMisdelvFreight === "Y") {
        return value === "Y" || recoveredFreightCustomerProvidedBol === "Y";
      }
      return true;
    }
  ),

  // SAIA BOL confirm billing - required when SAIA BOL is selected
  saiaMisdelvBolConfirmBilling: Yup.string().trim().when("recoveredFreightSaiaMisdelvBol", {
    is: "Y",
    then: (schema) => schema.required(CHECK_BOX_MSG).oneOf(["Y"], CHECK_BOX_MSG),
    otherwise: (schema) => schema.notRequired(),
  }),

  // SAIA BOL confirm billing detail
  saiaMisdelvBolConfirmBillingDetail: Yup.string().trim()
    .max(MAX_TEXT_LENGTH, `Must be at most ${MAX_TEXT_LENGTH} characters`)
    .when(["recoveredFreightSaiaMisdelvBol", "saiaMisdelvBolConfirmBilling"], {
      is: (saiaBol: string, confirmBilling: string) => saiaBol === "Y" && confirmBilling === "Y",
      then: (schema) => schema.required(REQUIRED_MSG),
      otherwise: (schema) => schema.notRequired(),
    }),

  // SAIA BOL confirmed imaged - required when SAIA BOL is selected
  saiaMisdelvBolConfirmImaged: Yup.string().trim().when("recoveredFreightSaiaMisdelvBol", {
    is: "Y",
    then: (schema) => schema.required(CHECK_BOX_MSG).oneOf(["Y"], CHECK_BOX_MSG),
    otherwise: (schema) => schema.notRequired(),
  }),

  // SAIA BOL confirmed imaged detail
  saiaMisdelvBolConfirmImagedDetail: Yup.string().trim()
    .max(MAX_TEXT_LENGTH, `Must be at most ${MAX_TEXT_LENGTH} characters`)
    .when(["recoveredFreightSaiaMisdelvBol", "saiaMisdelvBolConfirmImaged"], {
      is: (saiaBol: string, confirmImaged: string) => saiaBol === "Y" && confirmImaged === "Y",
      then: (schema) => schema.required(REQUIRED_MSG),
      otherwise: (schema) => schema.notRequired(),
    }),

  // Customer BOL confirm billing - required when Customer BOL is selected
  customerProvidedBolConfirmBilling: Yup.string().trim().when("recoveredFreightCustomerProvidedBol", {
    is: "Y",
    then: (schema) => schema.required(CHECK_BOX_MSG).oneOf(["Y"], CHECK_BOX_MSG),
    otherwise: (schema) => schema.notRequired(),
  }),

  // Customer BOL confirm billing detail
  customerProvidedBolConfirmBillingDetail: Yup.string().trim()
    .max(MAX_TEXT_LENGTH, `Must be at most ${MAX_TEXT_LENGTH} characters`)
    .when(["recoveredFreightCustomerProvidedBol", "customerProvidedBolConfirmBilling"], {
      is: (customerBol: string, confirmBilling: string) => customerBol === "Y" && confirmBilling === "Y",
      then: (schema) => schema.required(REQUIRED_MSG),
      otherwise: (schema) => schema.notRequired(),
    }),

  // Customer BOL confirmed imaged - required when Customer BOL is selected
  customerProvidedBolConfirmImaged: Yup.string().trim().when("recoveredFreightCustomerProvidedBol", {
    is: "Y",
    then: (schema) => schema.required(CHECK_BOX_MSG).oneOf(["Y"], CHECK_BOX_MSG),
    otherwise: (schema) => schema.notRequired(),
  }),

  // Customer BOL confirmed imaged detail
  customerProvidedBolConfirmImagedDetail: Yup.string().trim()
    .max(MAX_TEXT_LENGTH, `Must be at most ${MAX_TEXT_LENGTH} characters`)
    .when(["recoveredFreightCustomerProvidedBol", "customerProvidedBolConfirmImaged"], {
      is: (customerBol: string, confirmImaged: string) => customerBol === "Y" && confirmImaged === "Y",
      then: (schema) => schema.required(REQUIRED_MSG),
      otherwise: (schema) => schema.notRequired(),
    }),

  recoveredFreightNewProAllShort: Yup.string().trim().when("didSaiaRecoverMisdelvFreight", {
    is: "Y",
    then: (schema) => schema.required(OPTION_MSG),
    otherwise: (schema) => schema.notRequired(),
  }),

  // 86 for NEW PRO shows PRO or NB/OV - required when recovered = Y
  recoveredFreightNewProShowsProOrNbOv: Yup.string().trim().when("didSaiaRecoverMisdelvFreight", {
    is: "Y",
    then: (schema) => schema.required(CHECK_BOX_MSG).oneOf(["Y"], CHECK_BOX_MSG),
    otherwise: (schema) => schema.notRequired(),
  }),

  // When recoveredFreightNewProShowsProOrNbOv is "Y"
  newProShowsProOrNbOvDetail: Yup.string().trim()
    .max(MAX_TEXT_LENGTH, `Must be at most ${MAX_TEXT_LENGTH} characters`)
    .when("recoveredFreightNewProShowsProOrNbOv", {
      is: "Y",
      then: (schema) => schema.required(REQUIRED_MSG),
      otherwise: (schema) => schema.notRequired(),
    }),

  // Confirm DR imaged - required when recovered = Y
  recoveredFreightConfirmDrImaged: Yup.string().trim().when("didSaiaRecoverMisdelvFreight", {
    is: "Y",
    then: (schema) => schema.required(CHECK_BOX_MSG).oneOf(["Y"], CHECK_BOX_MSG),
    otherwise: (schema) => schema.notRequired(),
  }),

  // When recoveredFreightConfirmDrImaged is "Y"
  drImagedSignedShowingAppliesToProOrNbOv: Yup.string().trim().when("recoveredFreightConfirmDrImaged", {
    is: "Y",
    then: (schema) => schema.required(OPTION_MSG),
    otherwise: (schema) => schema.notRequired(),
  }),

  // When recoveredFreightConfirmDrImaged is "Y" AND drImagedSignedShowingAppliesToProOrNbOv === "Y"
  signedAppliesToProOrNbOvExceptionNumber: Yup.string().trim()
    .max(MAX_TEXT_LENGTH, `Must be at most ${MAX_TEXT_LENGTH} characters`)
    .when(["recoveredFreightConfirmDrImaged", "drImagedSignedShowingAppliesToProOrNbOv"], {
      is: (confirmDrImaged: string, signedShowing: string) =>
        confirmDrImaged === "Y" && signedShowing === "Y",
      then: (schema) => schema.required(REQUIRED_MSG),
      otherwise: (schema) => schema.notRequired(),
    }),

  // When didSaiaRecoverMisdelvFreight !== "N"
  didMisdelvPickupLeaveConsigneeShort: Yup.string().trim().when(
    "didSaiaRecoverMisdelvFreight",
    {
      is: (val: string) => val !== "N",
      then: (schema) => schema.required(OPTION_MSG),
      otherwise: (schema) => schema.notRequired(),
    }
  ),

  // When didMisdelvPickupLeaveConsigneeShort === "N"
  misdelvPickupConsigneeShortAdditionalComments: Yup.string().trim()
    .max(MAX_TEXT_LENGTH, `Must be at most ${MAX_TEXT_LENGTH} characters`)
    .when(
      ["didSaiaRecoverMisdelvFreight", "didMisdelvPickupLeaveConsigneeShort"],
      {
        is: (recovered: string, puLeave: string) =>
          recovered !== "N" && puLeave === "N",
        then: (schema) => schema.required(REQUIRED_MSG),
        otherwise: (schema) => schema.notRequired(),
      }
    ),

  // When didMisdelvPickupLeaveConsigneeShort === "Y"
  didRepresentativeUpdateDeliveryStatus: Yup.string().trim().when(
    ["didSaiaRecoverMisdelvFreight", "didMisdelvPickupLeaveConsigneeShort"],
    {
      is: (recovered: string, puLeave: string) =>
        recovered !== "N" && puLeave === "Y",
      then: (schema) => schema.required(OPTION_MSG),
      otherwise: (schema) => schema.notRequired(),
    }
  ),

  // When didRepresentativeUpdateDeliveryStatus === "N"
  representativeDeliveryStatusAdditionalComments: Yup.string().trim()
    .max(MAX_TEXT_LENGTH, `Must be at most ${MAX_TEXT_LENGTH} characters`)
    .when(
      [
        "didSaiaRecoverMisdelvFreight",
        "didMisdelvPickupLeaveConsigneeShort",
        "didRepresentativeUpdateDeliveryStatus",
      ],
      {
        is: (recovered: string, puLeave: string, repUpdate: string) =>
          recovered !== "N" && puLeave === "Y" && repUpdate === "N",
        then: (schema) => schema.required(REQUIRED_MSG),
        otherwise: (schema) => schema.notRequired(),
      }
    ),

  // When didSaiaRecoverMisdelvFreight !== "N"
  didMisdelvFreightMoveCorrectly: Yup.string().trim().when(
    "didSaiaRecoverMisdelvFreight",
    {
      is: (val: string) => val !== "N",
      then: (schema) => schema.required(OPTION_MSG),
      otherwise: (schema) => schema.notRequired(),
    }
  ),

  // When didMisdelvFreightMoveCorrectly === "Y" - must select Original PRO or Suffix PRO
  misdelvFreightMoveOriginalPro: Yup.string().trim().test(
    "pro-type-required",
    OPTION_MSG,
    function (value) {
      const { didSaiaRecoverMisdelvFreight, didMisdelvFreightMoveCorrectly, misdelvFreightMoveSuffixPro } = this.parent;
      // If move correctly is Y, at least one PRO type must be selected
      if (didSaiaRecoverMisdelvFreight !== "N" && didMisdelvFreightMoveCorrectly === "Y") {
        return value === "Y" || misdelvFreightMoveSuffixPro === "Y";
      }
      return true;
    }
  ),

  // When didMisdelvFreightMoveCorrectly === "Y"
  correctConsigneeReceivedCompleteOrder: Yup.string().trim().when(
    ["didSaiaRecoverMisdelvFreight", "didMisdelvFreightMoveCorrectly"],
    {
      is: (recovered: string, moveCorrectly: string) =>
        recovered !== "N" && moveCorrectly === "Y",
      then: (schema) => schema.required(OPTION_MSG),
      otherwise: (schema) => schema.notRequired(),
    }
  ),

  // When correctConsigneeReceivedCompleteOrder === "N"
  correctConsigneeOrderDoesDeliveryStatusShowOpenShortage: Yup.string().trim().when(
    [
      "didSaiaRecoverMisdelvFreight",
      "didMisdelvFreightMoveCorrectly",
      "correctConsigneeReceivedCompleteOrder",
    ],
    {
      is: (recovered: string, moveCorrectly: string, received: string) =>
        recovered !== "N" && moveCorrectly === "Y" && received === "N",
      then: (schema) => schema.required(OPTION_MSG),
      otherwise: (schema) => schema.notRequired(),
    }
  ),

  // When didMisdelvFreightMoveCorrectly === "N"
  misdelvFreightMoveCorrectlyAdditionalComments: Yup.string().trim()
    .max(MAX_TEXT_LENGTH, `Must be at most ${MAX_TEXT_LENGTH} characters`)
    .when(
      ["didSaiaRecoverMisdelvFreight", "didMisdelvFreightMoveCorrectly"],
      {
        is: (recovered: string, moveCorrectly: string) =>
          recovered !== "N" && moveCorrectly === "N",
        then: (schema) => schema.required(REQUIRED_MSG),
        otherwise: (schema) => schema.notRequired(),
      }
    ),

  // Checkboxes - required when didSaiaRecoverMisdelvFreight !== "N"
  recoveredFreightConfirmOption14: Yup.string().trim().when("didSaiaRecoverMisdelvFreight", {
    is: (val: string) => val !== "N",
    then: (schema) => schema.required(CHECK_BOX_MSG).oneOf(["Y"], CHECK_BOX_MSG),
    otherwise: (schema) => schema.notRequired(),
  }),

  recoveredFreightConfirmF18NotesClosingStatement: Yup.string().trim().when(
    "didSaiaRecoverMisdelvFreight",
    {
      is: (val: string) => val !== "N",
      then: (schema) => schema.required(CHECK_BOX_MSG).oneOf(["Y"], CHECK_BOX_MSG),
      otherwise: (schema) => schema.notRequired(),
    }
  ),

  recoveredFreightMsdExceptionAppliesToPro: Yup.string().trim().when(
    "didSaiaRecoverMisdelvFreight",
    {
      is: (val: string) => val !== "N",
      then: (schema) => schema.required(CHECK_BOX_MSG).oneOf(["Y"], CHECK_BOX_MSG),
      otherwise: (schema) => schema.notRequired(),
    }
  ),
});

export interface UseMisdeliveryAuditFormOptions {
  osdNumber?: string | number;
  initialData?: Partial<MisdeliveryAuditFormValues>;
  onSave?: (values: MisdeliveryAuditFormValues) => Promise<void> | void;
  onComplete?: (values: MisdeliveryAuditFormValues) => Promise<void> | void;
}

export const useMisdeliveryAuditForm = (
  options: UseMisdeliveryAuditFormOptions = {}
) => {
  const { osdNumber, initialData, onSave, onComplete } = options;

  const initialValues = useMemo(
    () => ({
      ...misdeliveryAuditInitialValues,
      ...initialData,
    }),
    [initialData]
  );

  const formik = useFormik<MisdeliveryAuditFormValues>({
    initialValues,
    enableReinitialize: true,
    validationSchema: misdeliveryAuditValidationSchema,
    validateOnBlur: true,
    validateOnChange: true,
    onSubmit: () => undefined,
  });

  const { values, setFieldValue } = formik;

  // Reset dependent fields when parent radio values change
  useEffect(() => {
    const recoveredValue = values.didSaiaRecoverMisdelvFreight;

    // When recovered is not "N", clear fields specific to "N"
    if (recoveredValue !== "N") {
      if (values.didSaiaRecoverMisdelvFreightWhy) setFieldValue("didSaiaRecoverMisdelvFreightWhy", "", false);
      if (values.notRecoveredFreightStatementOrDrImaged)
        setFieldValue("notRecoveredFreightStatementOrDrImaged", "", false);
    }

    // When recovered is "N", clear fields for Y and non-N scenarios
    if (recoveredValue === "N") {
      if (values.didMisdelvPickupLeaveConsigneeShort)
        setFieldValue("didMisdelvPickupLeaveConsigneeShort", "", false);
      if (values.misdelvPickupConsigneeShortAdditionalComments)
        setFieldValue(
          "misdelvPickupConsigneeShortAdditionalComments",
          "",
          false
        );
      if (values.didRepresentativeUpdateDeliveryStatus)
        setFieldValue(
          "didRepresentativeUpdateDeliveryStatus",
          "",
          false
        );
      if (values.representativeDeliveryStatusAdditionalComments)
        setFieldValue(
          "representativeDeliveryStatusAdditionalComments",
          "",
          false
        );
      if (values.didMisdelvFreightMoveCorrectly)
        setFieldValue("didMisdelvFreightMoveCorrectly", "", false);
      if (values.misdelvFreightMoveOriginalPro)
        setFieldValue("misdelvFreightMoveOriginalPro", "", false);
      if (values.misdelvFreightMoveSuffixPro)
        setFieldValue("misdelvFreightMoveSuffixPro", "", false);
      if (values.correctConsigneeReceivedCompleteOrder)
        setFieldValue("correctConsigneeReceivedCompleteOrder", "", false);
      if (values.correctConsigneeOrderDoesDeliveryStatusShowOpenShortage)
        setFieldValue("correctConsigneeOrderDoesDeliveryStatusShowOpenShortage", "", false);
      if (values.misdelvFreightMoveCorrectlyAdditionalComments)
        setFieldValue(
          "misdelvFreightMoveCorrectlyAdditionalComments",
          "",
          false
        );
      if (values.recoveredFreightConfirmOption14)
        setFieldValue("recoveredFreightConfirmOption14", "", false);
      if (values.recoveredFreightConfirmF18NotesClosingStatement)
        setFieldValue("recoveredFreightConfirmF18NotesClosingStatement", "", false);
      if (values.recoveredFreightMsdExceptionAppliesToPro)
        setFieldValue("recoveredFreightMsdExceptionAppliesToPro", "", false);
    }

    // When recovered is not "Y", clear Y-specific fields
    if (recoveredValue !== "Y") {
      if (values.recoveredFreightNewProNumber) setFieldValue("recoveredFreightNewProNumber", "", false);
      if (values.recoveredFreightSaiaMisdelvBol) setFieldValue("recoveredFreightSaiaMisdelvBol", "", false);
      if (values.recoveredFreightCustomerProvidedBol) setFieldValue("recoveredFreightCustomerProvidedBol", "", false);
      if (values.recoveredFreightNewProAllShort)
        setFieldValue("recoveredFreightNewProAllShort", "", false);
      if (values.recoveredFreightConfirmDrImaged)
        setFieldValue("recoveredFreightConfirmDrImaged", "", false);
      if (values.drImagedSignedShowingAppliesToProOrNbOv)
        setFieldValue("drImagedSignedShowingAppliesToProOrNbOv", "", false);
      if (values.signedAppliesToProOrNbOvExceptionNumber) setFieldValue("signedAppliesToProOrNbOvExceptionNumber", "", false);
    }

    // Clear SAIA BOL children when SAIA BOL is not selected
    if (values.recoveredFreightSaiaMisdelvBol !== "Y") {
      if (values.saiaMisdelvBolConfirmBilling)
        setFieldValue("saiaMisdelvBolConfirmBilling", "", false);
      if (values.saiaMisdelvBolConfirmImaged)
        setFieldValue("saiaMisdelvBolConfirmImaged", "", false);
    }

    // Clear Customer BOL children when Customer BOL is not selected
    if (values.recoveredFreightCustomerProvidedBol !== "Y") {
      if (values.customerProvidedBolConfirmBilling)
        setFieldValue("customerProvidedBolConfirmBilling", "", false);
      if (values.customerProvidedBolConfirmImaged)
        setFieldValue("customerProvidedBolConfirmImaged", "", false);
    }

    // Clear SAIA BOL details when confirmBilling is not "Y"
    if (values.saiaMisdelvBolConfirmBilling !== "Y" && values.saiaMisdelvBolConfirmBillingDetail) {
      setFieldValue("saiaMisdelvBolConfirmBillingDetail", "", false);
    }

    // Clear SAIA BOL imaged details when confirmedImaged is not "Y"
    if (values.saiaMisdelvBolConfirmImaged !== "Y" && values.saiaMisdelvBolConfirmImagedDetail) {
      setFieldValue("saiaMisdelvBolConfirmImagedDetail", "", false);
    }

    // Clear Customer BOL details when confirmBilling is not "Y"
    if (values.customerProvidedBolConfirmBilling !== "Y" && values.customerProvidedBolConfirmBillingDetail) {
      setFieldValue("customerProvidedBolConfirmBillingDetail", "", false);
    }

    // Clear Customer BOL imaged details when confirmedImaged is not "Y"
    if (values.customerProvidedBolConfirmImaged !== "Y" && values.customerProvidedBolConfirmImagedDetail) {
      setFieldValue("customerProvidedBolConfirmImagedDetail", "", false);
    }

    // Clear newProShowsProOrNbOvDetail when checkbox not "Y"
    if (
      values.recoveredFreightNewProShowsProOrNbOv !== "Y" &&
      values.newProShowsProOrNbOvDetail
    ) {
      setFieldValue("newProShowsProOrNbOvDetail", "", false);
    }

    // Clear drImagedSignedShowingAppliesToProOrNbOv when recoveredFreightConfirmDrImaged is not "Y"
    if (values.recoveredFreightConfirmDrImaged !== "Y" && values.drImagedSignedShowingAppliesToProOrNbOv) {
      setFieldValue("drImagedSignedShowingAppliesToProOrNbOv", "", false);
    }

    // Clear signedAppliesToProOrNbOvExceptionNumber when not applicable
    if (
      (values.recoveredFreightConfirmDrImaged !== "Y" ||
        values.drImagedSignedShowingAppliesToProOrNbOv === "N") &&
      values.signedAppliesToProOrNbOvExceptionNumber
    ) {
      setFieldValue("signedAppliesToProOrNbOvExceptionNumber", "", false);
    }

    // Clear misdelvPickupConsigneeShortAdditionalComments when not "N"
    if (
      values.didMisdelvPickupLeaveConsigneeShort !== "N" &&
      values.misdelvPickupConsigneeShortAdditionalComments
    ) {
      setFieldValue(
        "misdelvPickupConsigneeShortAdditionalComments",
        "",
        false
      );
    }

    // Clear representative update fields when puLeave is not "Y"
    if (values.didMisdelvPickupLeaveConsigneeShort !== "Y") {
      if (values.didRepresentativeUpdateDeliveryStatus)
        setFieldValue(
          "didRepresentativeUpdateDeliveryStatus",
          "",
          false
        );
      if (values.representativeDeliveryStatusAdditionalComments)
        setFieldValue(
          "representativeDeliveryStatusAdditionalComments",
          "",
          false
        );
    }

    // Clear representative additional details when not "N"
    if (
      values.didRepresentativeUpdateDeliveryStatus !== "N" &&
      values.representativeDeliveryStatusAdditionalComments
    ) {
      setFieldValue(
        "representativeDeliveryStatusAdditionalComments",
        "",
        false
      );
    }

    // Clear freight move additional comments when not "N"
    if (
      values.didMisdelvFreightMoveCorrectly !== "N" &&
      values.misdelvFreightMoveCorrectlyAdditionalComments
    ) {
      setFieldValue(
        "misdelvFreightMoveCorrectlyAdditionalComments",
        "",
        false
      );
    }

    // Clear Y-specific freight move fields when not "Y"
    if (values.didMisdelvFreightMoveCorrectly !== "Y") {
      if (values.misdelvFreightMoveOriginalPro)
        setFieldValue("misdelvFreightMoveOriginalPro", "", false);
      if (values.misdelvFreightMoveSuffixPro)
        setFieldValue("misdelvFreightMoveSuffixPro", "", false);
      if (values.correctConsigneeReceivedCompleteOrder)
        setFieldValue("correctConsigneeReceivedCompleteOrder", "", false);
      if (values.correctConsigneeOrderDoesDeliveryStatusShowOpenShortage)
        setFieldValue("correctConsigneeOrderDoesDeliveryStatusShowOpenShortage", "", false);
    }

    // Clear correctConsigneeOrderDoesDeliveryStatusShowOpenShortage when consignee received is not "N"
    if (
      values.correctConsigneeReceivedCompleteOrder !== "N" &&
      values.correctConsigneeOrderDoesDeliveryStatusShowOpenShortage
    ) {
      setFieldValue("correctConsigneeOrderDoesDeliveryStatusShowOpenShortage", "", false);
    }
  }, [
    setFieldValue,
    values.didSaiaRecoverMisdelvFreight,
    values.didSaiaRecoverMisdelvFreightWhy,
    values.notRecoveredFreightStatementOrDrImaged,
    values.recoveredFreightNewProNumber,
    values.recoveredFreightSaiaMisdelvBol,
    values.saiaMisdelvBolConfirmBilling,
    values.saiaMisdelvBolConfirmBillingDetail,
    values.saiaMisdelvBolConfirmImaged,
    values.saiaMisdelvBolConfirmImagedDetail,
    values.recoveredFreightCustomerProvidedBol,
    values.customerProvidedBolConfirmBilling,
    values.customerProvidedBolConfirmBillingDetail,
    values.customerProvidedBolConfirmImaged,
    values.customerProvidedBolConfirmImagedDetail,
    values.recoveredFreightNewProAllShort,
    values.recoveredFreightNewProShowsProOrNbOv,
    values.newProShowsProOrNbOvDetail,
    values.recoveredFreightConfirmDrImaged,
    values.drImagedSignedShowingAppliesToProOrNbOv,
    values.signedAppliesToProOrNbOvExceptionNumber,
    values.didMisdelvPickupLeaveConsigneeShort,
    values.misdelvPickupConsigneeShortAdditionalComments,
    values.didRepresentativeUpdateDeliveryStatus,
    values.representativeDeliveryStatusAdditionalComments,
    values.didMisdelvFreightMoveCorrectly,
    values.misdelvFreightMoveOriginalPro,
    values.misdelvFreightMoveSuffixPro,
    values.correctConsigneeReceivedCompleteOrder,
    values.correctConsigneeOrderDoesDeliveryStatusShowOpenShortage,
    values.misdelvFreightMoveCorrectlyAdditionalComments,
    values.recoveredFreightConfirmOption14,
    values.recoveredFreightConfirmF18NotesClosingStatement,
    values.recoveredFreightMsdExceptionAppliesToPro,
  ]);

  // Handle save - no validation required, calls API with action='save'
  const handleSave = useCallback(async () => {
    if (osdNumber) {
      const payload = transformFormValuesToPayload(formik.values);
      await saveMisdeliveryAudit(osdNumber, "save", payload);
    }
    if (onSave) {
      await onSave(formik.values);
    }
  }, [formik.values, osdNumber, onSave]);

  // Handle complete - validate all fields, then calls API with action='complete'
  // Returns true if validation passes and API is called, false otherwise
  const handleComplete = useCallback(async (): Promise<boolean> => {
    const errors = await formik.validateForm();
    formik.setTouched(
      Object.keys(formik.values).reduce(
        (acc, key) => ({ ...acc, [key]: true }),
        {}
      )
    );

    if (Object.keys(errors).length === 0) {
      if (osdNumber) {
        const payload = transformFormValuesToPayload(formik.values);
        await saveMisdeliveryAudit(osdNumber, "complete", payload);
      }
      if (onComplete) {
        await onComplete(formik.values);
      }
      return true;
    }
    return false;
  }, [formik, osdNumber, onComplete]);

  // Handle cancel - reset form to initial values
  const handleCancel = useCallback(() => {
    formik.resetForm({ values: initialValues });
  }, [formik, initialValues]);

  return {
    formik,
    handleSave,
    handleComplete,
    handleCancel,
    initialValues,
  };
};
